/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { hydrateFromRuntime } from "@/lib/feature-flags";
import { Capacitor } from "@capacitor/core";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FlagRow {
  flag_key: string;
  enabled: boolean;
  value_type: string;
  value_text: string | null;
  rollout_percent: number | null;
  organization_id: string | null;
  expires_at: string | null;
}

/** Resolved flag value after merging global default + org override */
export interface ResolvedFlag {
  enabled: boolean;
  value: string | null;
  source: "global" | "org_override" | "env";
}

type FlagMap = Record<string, ResolvedFlag>;

interface FeatureFlagContextValue {
  /** Resolved flags (global defaults merged with org overrides) */
  flags: FlagMap;
  /** Whether the flags have been loaded from the database */
  isLoaded: boolean;
  /**
   * Whether `flags` belongs to the organization currently active. After an org switch the
   * previous org's map stays in place until the new org's rows arrive, so anything that
   * must not act on another org's flags (e.g. What's New's flag-gated entries) checks this.
   */
  isCurrentForOrganization: boolean;
  /**
   * Whether the current organization's flag read has finished: its flags arrived
   * (`isCurrentForOrganization`) or the read failed, leaving the env fallbacks in force.
   */
  isSettled: boolean;
  /** Check if a flag is enabled */
  isEnabled: (key: string) => boolean;
  /** Get the string value for an enum/json flag */
  getValue: (key: string) => string | null;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  flags: {},
  isLoaded: false,
  isCurrentForOrganization: false,
  isSettled: false,
  isEnabled: () => false,
  getValue: () => null,
});

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const REFETCH_INTERVAL = 5 * 60 * 1000; // 5 minutes

/** Deterministic 0-99 bucket for a string, so the same org always lands in the same bucket for a given flag. */
export function bucketOf(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % 100;
}

/** Resolve a row's effective enabled state, gating `percentage`-type flags by a deterministic org bucket. */
export function resolveRowEnabled(row: FlagRow, organizationId: string | null): boolean {
  if (row.value_type !== "percentage") return row.enabled;
  if (!row.enabled || row.rollout_percent == null || !organizationId) return false;
  return bucketOf(`${row.flag_key}:${organizationId}`) < row.rollout_percent;
}

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const { organizationId } = useOrganization();
  const [resolvedFlags, setResolvedFlags] = useState<FlagMap>({});
  const [resolvedForOrganizationId, setResolvedForOrganizationId] = useState<string | null>(null);
  // The static runtime store outlives this provider (logout unmounts it); empty it on teardown
  // so the next session never starts from the previous one's flags.
  useEffect(() => () => hydrateFromRuntime({}), []);

  const { data: rows, isSuccess, isError } = useQuery({
    queryKey: ["feature-flags", organizationId],
    queryFn: async () => {
      // Fetch global flags + org-specific overrides in one query
      const { data, error } = await supabase
        .from("feature_flags")
        .select("flag_key, enabled, value_type, value_text, rollout_percent, organization_id, expires_at");

      if (error) {
        console.warn("[FeatureFlagProvider] Failed to fetch flags, using env fallbacks:", error.message);
        return [] as FlagRow[];
      }
      return (data || []) as FlagRow[];
    },
    // Wait for auth to resolve (organizationId is set after auth + org fetch)
    enabled: !!organizationId,
    staleTime: STALE_TIME,
    refetchInterval: REFETCH_INTERVAL,
    // Don't block rendering on flag fetches
    refetchOnWindowFocus: false,
    // On native, CapacitorHttp can be slower — retry more aggressively
    retry: Capacitor.isNativePlatform() ? 3 : 1,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
  });

  useEffect(() => {
    if (!rows) return;

    const now = new Date();
    const merged: FlagMap = {};

    // First pass: apply global defaults (org_id IS NULL)
    for (const row of rows) {
      if (row.organization_id !== null) continue;
      if (row.expires_at && new Date(row.expires_at) < now) continue;
      merged[row.flag_key] = {
        enabled: resolveRowEnabled(row, organizationId),
        value: row.value_text,
        source: "global",
      };
    }

    // Second pass: apply org-specific overrides
    if (organizationId) {
      for (const row of rows) {
        if (row.organization_id !== organizationId) continue;
        if (row.expires_at && new Date(row.expires_at) < now) continue;
        merged[row.flag_key] = {
          enabled: resolveRowEnabled(row, organizationId),
          value: row.value_text,
          source: "org_override",
        };
      }
    }

    setResolvedFlags(merged);
    setResolvedForOrganizationId(organizationId ?? null);

    // Sync into the static module so non-React code can read flags
    const plain: Record<string, { enabled: boolean; value: string | null }> = {};
    for (const [k, v] of Object.entries(merged)) {
      plain[k] = { enabled: v.enabled, value: v.value };
    }
    hydrateFromRuntime(plain);
  }, [rows, organizationId]);

  const isEnabled = (key: string): boolean => {
    return resolvedFlags[key]?.enabled ?? false;
  };

  const getValue = (key: string): string | null => {
    return resolvedFlags[key]?.value ?? null;
  };

  const isCurrentForOrganization =
    isSuccess && !!organizationId && resolvedForOrganizationId === organizationId;

  return (
    <FeatureFlagContext.Provider
      value={{
        flags: resolvedFlags,
        isLoaded: isSuccess,
        isCurrentForOrganization,
        isSettled: isCurrentForOrganization || (!!organizationId && isError),
        isEnabled,
        getValue,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Access the runtime feature flag context */
export function useRuntimeFlags() {
  return useContext(FeatureFlagContext);
}